import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

/**
 * Hook to set up enhanced Sentry tracking with detailed device info
 * @param {Object} user - The authenticated user object (optional)
 * @param {Object} options - Configuration options
 * @returns {void}
 */
export const useSentrySetup = (user = null, options = {}) => {
  const {
    includePerformance = true,
    includeBattery = true,
    includeStorage = true,
    includeNetwork = true,
  } = options;

  useEffect(() => {
    // Skip entirely in development (already disabled in instrument.js)
    if (process.env.NODE_ENV !== 'production') {
      return;
    }
    const setupSentry = async () => {
      if (user) {
        Sentry.setUser({
          id: user.uid,
          email: user.email,
          name: user.displayName,
        });
      }

      // Basic device info
      const userAgent = navigator.userAgent;
      const platform = navigator.platform || 'unknown';
      const language = navigator.language || 'unknown';
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;
      const pixelRatio = window.devicePixelRatio || 1;
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const online = navigator.onLine;
      const hardwareConcurrency = navigator.hardwareConcurrency || 'unknown';
      const memory = navigator.deviceMemory || 'unknown';

      // Get OS details from userAgent
      const getOSDetails = () => {
        const ua = userAgent.toLowerCase();
        let os = 'unknown';
        let osVersion = 'unknown';

        if (ua.includes('windows')) {
          os = 'Windows';
          const match = ua.match(/windows nt (\d+\.\d+)/);
          osVersion = match ? match[1] : 'unknown';
        } else if (ua.includes('android')) {
          os = 'Android';
          const match = ua.match(/android (\d+(\.\d+)*)/);
          osVersion = match ? match[1] : 'unknown';
        } else if (
          ua.includes('iphone') ||
          ua.includes('ipad') ||
          ua.includes('ipod')
        ) {
          os = 'iOS';
          const match = ua.match(/os (\d+[_\d]*) like mac os/);
          osVersion = match ? match[1].replace(/_/g, '.') : 'unknown';
        } else if (ua.includes('mac')) {
          os = 'macOS';
          const match = ua.match(/mac os x (\d+[_\d]*)/);
          osVersion = match ? match[1].replace(/_/g, '.') : 'unknown';
        } else if (ua.includes('linux')) {
          os = 'Linux';
        }

        return { os, osVersion };
      };

      const { os, osVersion } = getOSDetails();

      // Service worker status
      const serviceWorkerStatus =
        'serviceWorker' in navigator
          ? navigator.serviceWorker.controller
            ? 'controlled'
            : 'registered'
          : 'unsupported';

      // Check if running as installed PWA
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone ||
        document.referrer.includes('android-app://');

      // Collect information objects to send to Sentry
      const deviceInfo = {
        userAgent,
        platform,
        language,
        memory,
        hardwareConcurrency,
        isTouchDevice,
        os,
        osVersion,
        screen: {
          width: screenWidth,
          height: screenHeight,
          pixelRatio,
          orientation: window.screen.orientation
            ? window.screen.orientation.type
            : 'unknown',
        },
      };

      const appInfo = {
        serviceWorker: serviceWorkerStatus,
        installedAsPWA: isStandalone,
        appVersion:
          global.appVersion || process.env.REACT_APP_VERSION || 'unknown',
      };

      // Network information
      if (includeNetwork) {
        const getConnectionInfo = () => {
          if ('connection' in navigator) {
            const conn = navigator.connection;
            return {
              type: conn.type || 'unknown',
              effectiveType: conn.effectiveType || 'unknown',
              downlinkMax: conn.downlinkMax || 'unknown',
              downlink: conn.downlink || 'unknown',
              rtt: conn.rtt || 'unknown',
              saveData: conn.saveData || false,
            };
          }
          return { type: 'unknown' };
        };

        const connectionInfo = getConnectionInfo();
        deviceInfo.network = {
          online,
          ...connectionInfo,
        };

        // Set network-related tags
        Sentry.setTag('networkType', connectionInfo.type || 'unknown');
        Sentry.setTag(
          'networkEffectiveType',
          connectionInfo.effectiveType || 'unknown',
        );
        Sentry.setTag('isOnline', online.toString());
      } else {
        deviceInfo.network = { online };
      }

      // Battery information
      if (includeBattery) {
        const getBatteryInfo = async () => {
          if ('getBattery' in navigator) {
            try {
              const battery = await navigator.getBattery();
              return {
                level: battery.level,
                charging: battery.charging,
                chargingTime: battery.chargingTime,
                dischargingTime: battery.dischargingTime,
              };
            } catch (e) {
              return { status: 'error', error: e.message };
            }
          }
          return { status: 'unsupported' };
        };

        const batteryInfo = await getBatteryInfo();
        deviceInfo.battery = batteryInfo;

        if (
          batteryInfo.status !== 'unsupported' &&
          batteryInfo.status !== 'error'
        ) {
          Sentry.setTag(
            'batteryLevel',
            batteryInfo.level
              ? batteryInfo.level < 0.2
                ? 'low'
                : batteryInfo.level < 0.5
                ? 'medium'
                : 'high'
              : 'unknown',
          );
          Sentry.setTag(
            'batteryCharging',
            batteryInfo.charging?.toString() || 'unknown',
          );
        }
      }

      // Storage information
      if (includeStorage) {
        const getStorageInfo = async () => {
          if ('storage' in navigator && 'estimate' in navigator.storage) {
            try {
              const estimate = await navigator.storage.estimate();
              return {
                quota: estimate.quota,
                usage: estimate.usage,
                usagePercentage: estimate.quota
                  ? Math.round((estimate.usage / estimate.quota) * 100)
                  : null,
                remaining: estimate.quota - estimate.usage,
              };
            } catch (e) {
              return { status: 'error', error: e.message };
            }
          }
          return { status: 'unsupported' };
        };

        const storageInfo = await getStorageInfo();
        appInfo.storageInfo = storageInfo;

        if (
          storageInfo.status !== 'unsupported' &&
          storageInfo.status !== 'error'
        ) {
          Sentry.setTag(
            'storageUsage',
            storageInfo.usagePercentage != null
              ? storageInfo.usagePercentage > 90
                ? 'critical'
                : storageInfo.usagePercentage > 70
                ? 'high'
                : storageInfo.usagePercentage > 50
                ? 'medium'
                : 'low'
              : 'unknown',
          );
        }
      }

      // Performance metrics
      if (includePerformance) {
        const getPerformanceMetrics = () => {
          if ('performance' in window) {
            const perf = window.performance;
            const navTiming = perf.timing || {};

            // Get any available performance entries
            const paintMetrics = {};
            if ('getEntriesByType' in perf) {
              const paintEntries = perf.getEntriesByType('paint');
              paintEntries.forEach((entry) => {
                paintMetrics[entry.name] = entry.startTime;
              });
            }

            return {
              paintMetrics,
              memory: perf.memory
                ? {
                    jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
                    totalJSHeapSize: perf.memory.totalJSHeapSize,
                    usedJSHeapSize: perf.memory.usedJSHeapSize,
                  }
                : 'unavailable',
              timing: navTiming.loadEventEnd
                ? {
                    loadEventEnd:
                      navTiming.loadEventEnd - navTiming.navigationStart,
                    domContentLoaded:
                      navTiming.domContentLoadedEventEnd -
                      navTiming.navigationStart,
                    firstByte:
                      navTiming.responseStart - navTiming.navigationStart,
                    dnsLookup:
                      navTiming.domainLookupEnd - navTiming.domainLookupStart,
                    tcpConnect: navTiming.connectEnd - navTiming.connectStart,
                    requestStart:
                      navTiming.requestStart - navTiming.navigationStart,
                  }
                : 'unavailable',
            };
          }
          return { status: 'unsupported' };
        };

        const performanceMetrics = getPerformanceMetrics();
        Sentry.setContext('performance', performanceMetrics);

        if (
          performanceMetrics.timing !== 'unavailable' &&
          performanceMetrics.timing?.loadEventEnd
        ) {
          Sentry.setTag(
            'pageLoadTime',
            performanceMetrics.timing.loadEventEnd > 5000
              ? 'slow'
              : performanceMetrics.timing.loadEventEnd > 2000
              ? 'medium'
              : 'fast',
          );
        }
      }

      // Set all the context information in Sentry
      Sentry.setContext('device', deviceInfo);
      Sentry.setContext('app', appInfo);

      // Set useful tags for filtering
      Sentry.setTag('appVersion', appInfo.appVersion);
      Sentry.setTag('os', os);
      Sentry.setTag('osVersion', osVersion);
      Sentry.setTag('installedAsPWA', isStandalone.toString());
      Sentry.setTag(
        'serviceWorkerActive',
        (serviceWorkerStatus === 'controlled').toString(),
      );
      Sentry.setTag(
        'isMobile',
        /mobile|android|ios|iphone/i.test(userAgent).toString(),
      );
      Sentry.setTag(
        'isTablet',
        (
          /tablet|ipad/i.test(userAgent) ||
          (screenWidth > 768 && isTouchDevice)
        ).toString(),
      );
    };

    setupSentry().catch((err) => {
      console.error('Error setting up Sentry context:', err);
    });
  }, [
    user,
    includePerformance,
    includeBattery,
    includeStorage,
    includeNetwork,
  ]);
};
